import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Distributions / Move Money → Distribution (One-Time) / Distribution (Recurring).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Distribution (One-Time)", "One-Time
 * Distribution (Qualified)", "One-Time Distribution (Non-Qualified)",
 * "Distribution (Recurring)", "Recurring Distribution (Qualified)" and
 * "Recurring Distribution (Non-Qualified)". One-Time and Recurring ask the
 * same payout, Trading and Move Money questions; Recurring moves the amount
 * and withholding into a schedule panel on the second screen. Kept parallel
 * with Contributions.js. The One-Time question order below is the intended
 * order (Trading → capital gains → Move Money); see the guide-writing report
 * for the visibility-rule defects on the live One-Time screen. Shared chrome
 * and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Distributions / Move Money',
    subCategory: 'Distribution (One-Time) or Distribution (Recurring)',
    subject: subjectRow('One-time distribution - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>One-Time and Recurring ask almost the same questions.</strong> The differences are called out
        in the reference below - mainly, Recurring asks for the amount, schedule and tax withholding on the
        second screen instead of the first.
      </>
    ),
  }),
  {
    title: 'Answer the Distribution Details questions',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>Distribution (One-Time)</strong> or <strong>Distribution (Recurring)</strong> screen asks
        about the payout, whether Trading needs to raise cash and whether there is a Move Money on file.
        Questions reveal themselves one at a time based on your answers, and every question in the reference
        below is required once it appears. Your answer to <em>Is this for a Qualified or Non-Qualified
        Account?</em> decides which screen comes next.
      </p>
    ),
  },
  {
    title: 'Describe the distribution and choose the account',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          <strong>One-Time:</strong> the next screen opens with a single box: <em>Please provide as much
          information as possible for the Operations Team...</em> Fill it in first - nothing else on the screen
          appears until you do. It becomes the case description Operations reads first. The{' '}
          <strong>Qualified Accounts</strong> or <strong>Non-Qualified Accounts</strong> table then appears;
          pick one row - the account the money is coming out of.
        </p>
        <p style={{ margin: 0 }}>
          <strong>Recurring:</strong> the next screen has a recurring schedule panel - the distribution amount,
          how often and from when (with an optional end), tax withholding, and a details box that becomes the
          case description - plus the same <strong>Qualified Accounts</strong> or{' '}
          <strong>Non-Qualified Accounts</strong> table. Pick one row.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the table (Recurring adds a <em>Recurring
        Distributions</em> authorization - see What happens next). Select <em>I have read, understand, and
        agree to the above information</em> under Acknowledgement and click Next. FSC creates the case and opens
        it. Note the case number for follow-up.
      </p>
    ),
  },
];

const MOVE_MONEY_OPTIONS = [
  'Yes (There is a Move Money on file)',
  'No (There is not a Move Money on file, I am adding one to use with this Case)',
  'Not Applicable (I understand if a Move Money is required but not on file it will result in a NIGO)',
];

const DIRECTION_OPTIONS = [
  'Setup ACH In (From Bank Account TO Account with Prosperity)',
  'Setup ACH OUT (From Prosperity Account TO Bank Account)',
  'Setup ACH IN and OUT (To/From Prosperity, To/From Bank Account)',
];

const QUALIFIED_TYPES = [
  'Normal', 'RMD', 'Early / Hardship', 'Direct Transfer/Rollover',
  'Early Distribution with Exception (SEPP, Rule of 55, etc.)',
  'Fully Qualified Distribution (Disability, First-Time Home Purchase, etc.)',
];

const BANK_FIELD = 'Which Bank should the funds be sent to? (If using a Bank thats on file, please provide the Bank name, If sending by Check or Wire, please enter the details here)';

const BANKING_NOTE = (
  <>
    A <strong>Banking Information</strong> section asks for: <em>Name of Banking Institution</em>,{' '}
    <em>Bank Account Type (Individual, Joint, Trust, etc...)</em>, <em>First Name (Bank Account Owner / Name on
    Account)</em>, <em>Last Name (Bank Account Owner / Name on Account)</em>, <em>Bank Account Number</em>,{' '}
    <em>Bank Account Routing Number</em> and the bank&rsquo;s address. If you said there is more than one person
    on the account, it also asks for <em>Joint First Name (Bank Account Owner / Name on Account)</em> and{' '}
    <em>Joint Last Name (Bank Account Owner / Name on Account)</em>. All are required. FSC saves these as new
    banking instructions linked to the case.
  </>
);

const QUESTIONS = [
  // One-Time, first screen
  {
    screen: 'Distribution (One-Time)',
    q: 'One-Time Distribution Amount',
    when: 'Always - this is the first question',
    note: 'The next question only appears once the amount is more than $1.',
  },
  {
    q: 'Is this for a Qualified or Non-Qualified Account?',
    when: 'Once the amount is entered',
    options: ['Qualified', 'Non-Qualified'],
    note: 'Decides whether the next screen lists Qualified or Non-Qualified accounts.',
  },
  {
    q: 'Qualified Distribution Type',
    when: 'Only if you chose Qualified',
    options: QUALIFIED_TYPES,
  },
  {
    q: 'Do you wish to Withhold Taxes?',
    when: 'Only for Qualified, once the distribution type is chosen',
    options: ['Yes (Withhold Taxes)', 'No (Do Not Withhold Taxes)'],
  },
  {
    q: 'Federal Tax · State Tax',
    when: 'Only if you chose to withhold taxes',
    note: 'Both are required. The fields do not say whether they want a dollar amount or a percentage; the Net Distribution line shown under them subtracts both figures from the amount as dollars. Check that line against what you told the client, and spell out the withholding in the details box on the next screen.',
    warn: true,
  },
  {
    q: 'How are the funds being dispersed?',
    when: 'For Non-Qualified straight away; for Qualified once the withholding question (and any tax figures) are answered',
    options: ['ACH', 'Wire', 'Check', 'Other'],
  },
  {
    q: BANK_FIELD,
    when: 'Once you have said how the funds are being dispersed',
    note: 'A free-text box - name the bank on file, or give the check or wire details.',
  },
  {
    q: 'Is there more than one person on the account? (Joint Owner, Trustee, POA, etc...)',
    when: 'Once the bank is entered',
    options: ['Yes (There is more than one person on the account)', 'No (There is not more than one person on the account)'],
    note: 'Yes adds joint-owner name fields to the Banking Information section if you add a new Move Money.',
  },
  {
    q: 'Do you need the Trading Team to raise cash for this Distribution, or are funds already available?',
    when: 'After the joint-owner question',
    options: ['Yes (I need the Trading Team to raise cash for this Distribution)', 'No (The Trading Team is not needed, Funds are available)'],
    note: 'Yes makes FSC create a Trading task from the case automatically when you submit.',
  },
  {
    q: 'Would you like to enter a Taxable Gains Threshold for this Transaction? (We strongly encourage this)',
    when: 'Only for a Non-Qualified account where Trading is raising cash',
    options: ['Yes (I would like to enter Capital Gain information)', 'No (I would not like to enter Capital Gain Information, I understand this could result in adverse tax consequences)'],
  },
  {
    q: 'Max Long-Term Gains ($) · Max Short-Term Gains ($)',
    when: 'Only if you chose to enter Capital Gain information',
    note: 'Dollar caps on the gains Trading may realise. Both are required once shown.',
  },
  {
    q: 'Capital Gains Notes (If None, please enter "None" or "N/A")',
    when: 'Once either gains cap is above zero',
  },
  {
    q: 'Is there a Move Money on file?',
    when: 'Once the Trading question (and, if shown, the capital gains questions) are answered',
    options: MOVE_MONEY_OPTIONS,
    note: 'Not Applicable lets you continue, but if a Move Money is needed and not on file the request is returned NIGO.',
    warn: true,
  },
  {
    q: 'Which Direction(s) Should Funds be allowed to Move?',
    when: 'Only if you are adding a new Move Money (the No answer above)',
    options: DIRECTION_OPTIONS,
    note: BANKING_NOTE,
  },
  {
    screen: 'One-Time Distribution (Qualified) / (Non-Qualified)',
    q: 'Please provide as much information as possible for the Operations Team...',
    when: 'Always - the first box on the second screen',
    note: 'This becomes the case description. The table, notice and Acknowledgement stay hidden until it has something in it.',
  },
  {
    q: 'Qualified Accounts / Non-Qualified Accounts (table)',
    when: 'Once the details box is filled in - you see the table matching your Qualified / Non-Qualified answer',
    note: 'Pick exactly one row - the sleeve the money is coming out of. Columns: Name, Account Details (Non-Qualified) or Account Information (Qualified), Model, Cash Balance and Balance.',
  },
  // Recurring, first screen
  {
    screen: 'Distribution (Recurring)',
    q: 'How are the funds being dispersed?',
    when: 'Always - this is the first question (there is no amount on this screen)',
    options: ['ACH', 'Wire', 'Check', 'Other'],
  },
  {
    q: 'Is there more than one person on the account? (Joint Owner, Trustee, POA, etc...)',
    when: 'Once you have said how the funds are being dispersed',
    options: ['Yes (There is more than one person on the account)', 'No (There is not more than one person on the account)'],
  },
  {
    q: 'Is this for a Qualified or Non-Qualified Account?',
    when: 'After the joint-owner question',
    options: ['Qualified', 'Non-Qualified'],
  },
  {
    q: 'Qualified Distribution Type',
    when: 'Only if you chose Qualified - same options as One-Time',
    options: QUALIFIED_TYPES,
  },
  {
    q: 'Do you need Trading to raise cash each month? (Please note, if this is for an SMA, the Trading Team will forward this request to the SMA Manager)',
    when: 'For Non-Qualified straight away; for Qualified once the distribution type is chosen',
    options: [
      'Yes (We need Trading to Raise Cash each month)',
      'No (We will let you know each time. We acknowledge it is our responsibility to reach out to the Trading Team each month to facilitate the Trades)',
    ],
    note: 'Yes makes FSC create a Trading task from the case automatically when you submit.',
  },
  {
    q: 'Would you like to enter a Taxable Gains Threshold for this Transaction, Sleeve, or Household? (We strongly encourage this)',
    when: 'Only if Trading is raising cash each month',
    options: [
      'Yes (I want to enter a Capital Gains threshold and apply it to the entire Household for this Year)',
      'Yes (I want to enter a Capital Gains threshold and apply it to this Sleeve for this Year)',
      'Yes (I want to enter a Capital Gains threshold but but for this transaction only)',
      'No (I Do NOT need to enter Capital Gain information - I realize this could result in tax consequences for this Household & Account/Sleeve)',
    ],
    note: 'A note under the question explains the levels: Household caps gains on all trades for the household for the rest of the year, Sleeve caps them for this sleeve for the rest of the year, and Transaction applies to this transaction only. It recommends Household, and says the caps apply only to Valor Models - Prosperity has no influence on SMAs.',
  },
  {
    q: 'Max Short-Term Gains ($) (Annual) · Max Long-Term Gains ($) (Annual)',
    when: 'Only if you chose one of the Yes threshold answers',
  },
  {
    q: 'Capital Gains Notes (If None please enter "None" or "N/A")',
    when: 'Once either gains cap is above zero',
  },
  {
    q: 'Is there a Move Money on file?',
    when: 'Once the Trading question (and, if shown, the capital gains questions) are answered',
    options: MOVE_MONEY_OPTIONS,
    note: 'Not Applicable lets you continue, but if a Move Money is needed and not on file the request is returned NIGO.',
    warn: true,
  },
  {
    q: BANK_FIELD,
    when: 'Only if you answered Yes or Not Applicable above',
  },
  {
    q: 'Which Direction(s) Should Funds be allowed to Move?',
    when: 'Only if you are adding a new Move Money (the No answer above)',
    options: DIRECTION_OPTIONS,
    note: BANKING_NOTE,
  },
  {
    screen: 'Recurring Distribution (Qualified) / (Non-Qualified)',
    q: 'Recurring schedule panel',
    when: 'Always on the second screen',
    note: 'Enter the distribution amount, the schedule (how often, and on which day) with a start date and an optional end date, whether to withhold taxes with the federal and state percentages, and the details. The details become the case description; with no end date the schedule runs until cancelled.',
  },
  {
    q: 'Qualified Accounts / Non-Qualified Accounts (table)',
    when: 'Always on the second screen - you see the table matching your Qualified / Non-Qualified answer',
    note: 'Pick exactly one row - the sleeve the distributions come out of. Columns: Name, Account Information, Model, Cash Balance and Balance.',
  },
];

const RECURRING_NOTE = (
  <Callout>
    <strong>Recurring distributions.</strong> The Recurring screens add a <em>Recurring Distributions</em>{' '}
    authorization: by submitting, you authorize Operations to process the distribution on the stated schedule
    until it is cancelled. It keeps running until an Advisor or CSA contacts Operations to change or stop it,
    and Prosperity is not responsible for a distribution that goes out before a stop instruction is received
    and processed. Returned or failed items can interrupt the schedule, and the submitting CSA / Advisor is
    responsible for confirming eligibility and requirements (e.g., Required Minimum Distributions, 5 Year
    Rule). FSC records the schedule against the selected sleeve and links it to the case.
  </Callout>
);

const TRADING_NOTE = (
  <Para>
    If you answered Yes to the Trading question, FSC also creates a Trading task from the case as soon as
    it is submitted.
  </Para>
);

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Distributions / Move Money'),
  {
    h: 'The questions stopped after the amount',
    p: ['On One-Time, the rest of the screen only appears once One-Time Distribution Amount is more than $1.'],
  },
  {
    h: 'The account table is empty, or the account I expected is missing',
    p: [
      'The Qualified Accounts table only lists qualified sleeves, and Non-Qualified Accounts only non-qualified ones - if the account is on the other side, go Back and change your answer to Is this for a Qualified or Non-Qualified Account?',
      'Neither table lists the custodial account row itself (the account number with no sleeve suffix) or the holding sleeves whose names end in Contribution or Distribution - pick the model sleeve the money should come from.',
    ],
  },
  troubleSleeveRows('Inactive sleeves, sleeves with a negative balance, and cancelled sleeves ending in CNX are filtered out of both tables.'),
  {
    h: "The table and notice haven't appeared",
    p: ['On the One-Time second screen they stay hidden until the details box at the top has something in it.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function Distributions() {
  return (
    <GuidePage
      title="Distribution Requests"
      icon="box-arrow-up"
      path="Operations → Distributions / Move Money → Distribution (One-Time) or Distribution (Recurring)"
      minutes={6}
      intro="How to ask the Prosperity Operations team to send money out of a client account - once or on a schedule - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers, and some paths need all of them.</Para>
        <Bullets items={[
          <><strong>The account the money is coming out of</strong>, and whether it is qualified or non-qualified. You pick the sleeve from a list, so you do not need account numbers memorised.</>,
          <><strong>The amount.</strong> For Recurring, also the schedule - how often, the start date and any end date.</>,
          <><strong>For a qualified account</strong>, the distribution type and the tax withholding decision, with federal and state figures if the client wants tax withheld.</>,
          <><strong>How the money is being paid out</strong> - ACH, wire, check or other - and where it is going.</>,
          <><strong>Whether Trading needs to raise cash</strong>, and if so any capital gains caps the client wants.</>,
          <><strong>The Move Money position.</strong> If one is on file, the bank name. If you are adding one, the bank name, account type, owner name(s), account and routing numbers and the bank&rsquo;s address.</>,
        ]} />
        <Callout tone="warn">
          Operations and Trading cannot raise cash from, or distribute from, an account with outstanding
          Prosperity paperwork - the ticket is returned NIGO until the account is back in good order.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up. One-Time questions come first, then Recurring.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" before={<>{TRADING_NOTE}{RECURRING_NOTE}</>} />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not tax advice, and it does not describe distribution
        eligibility, withholding requirements or the tax consequences for the client.
      </Footnote>
    </GuidePage>
  );
}
